// Implements: F-MCK-015, F-UIX-010
/**
 * A tag as the person typed it, checked before it leaves the browser.
 *
 * `normalizeTag` follows the backend's order (`domain/value_objects/tag.py`): NFC, case fold, trim,
 * collapse inner whitespace. JavaScript has no `casefold`, so `toLowerCase` stands in; the server
 * normalises again and its answer is what the page shows.
 *
 * `tagProblem` catches only what the browser can judge for sure: an empty tag, one over the
 * 32-character bound, a tag that is `.` or `..`, which a URL path segment cannot carry (a
 * browser folds `.` and `..`, escaped or not, before the request leaves), and a tag with a `/`,
 * which the tag routes cannot name either (`%2F` is decoded before routing). The server refuses
 * all three too. The rest of the repertoire is the server's to decide: a stricter copy here could
 * refuse a tag it accepts.
 */

export type TagProblem = "empty" | "tooLong" | "dots" | "slash";

export const MAX_TAG_LENGTH = 32;

/** The length the backend measures (Python `len`): code points, not UTF-16 units. */
export function codePoints(text: string): number {
  return Array.from(text).length;
}

export function normalizeTag(raw: string): string {
  return raw.normalize("NFC").toLowerCase().trim().replace(/\s+/gu, " ");
}

export function tagProblem(raw: string): TagProblem | null {
  const tag = normalizeTag(raw);
  if (tag.length === 0) return "empty";
  if (codePoints(tag) > MAX_TAG_LENGTH) return "tooLong";
  if (tag === "." || tag === "..") return "dots";
  if (tag.includes("/")) return "slash";
  return null;
}
